<template>
  <div class="register-vehicle">
    <div class="form-container">
      <v-card class="section-card" elevation="0">
        <v-form ref="form" v-model="valid">
          <div class="form-header">
            <div class="step-circle">3</div>
            <h2 class="form-title">ข้อมูลรถและเอกสาร</h2>
          </div>

          <p class="muted mb-4">
            กรุณากรอกข้อมูลรถและอัพโหลดเอกสารที่เกี่ยวข้อง
            เพื่อใช้ในการตรวจสอบและยืนยันตัวตน
          </p>

          <v-divider class="mb-4"></v-divider>

          <v-row>
            <!-- ประเภทรถ -->
            <v-col cols="12">
              <span class="detail-text"
                ><span class="req">*</span>ประเภทรถ</span
              >
              <v-select
                v-model="form.vehicleTypeId"
                :items="vehicleTypes"
                @change="onVehicleTypeChange"
                item-text="text"
                item-value="value"
                placeholder="เลือกประเภทรถ"
                v-bind="selProps"
                :rules="[req]"
              >
                <template v-slot:append>
                  <img
                    :src="chev"
                    alt="chevron"
                    width="16"
                    height="16"
                    class="icon-rotate mt-1"
                  />
                </template>
              </v-select>
            </v-col>

            <!-- เลขทะเบียนรถ (ถ้ามี) -->
            <v-col cols="12" v-if="form.vehicleType && !isBicycle">
              <span class="detail-text">เลขทะเบียนรถ</span>
              <v-text-field
                v-model.trim="form.licensePlate"
                placeholder="เช่น กก 1234 กรุงเทพมหานคร"
                v-bind="tfProps"
              />
            </v-col>

            <!-- ยี่ห้อ / รุ่น / สี (ทุกประเภท) -->
            <v-col cols="12" sm="4" v-if="form.vehicleType">
              <span class="detail-text">ยี่ห้อ</span>
              <v-text-field
                v-model.trim="form.vehicleBrand"
                placeholder="เช่น Honda, Toyota"
                v-bind="tfProps"
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="form.vehicleType">
              <span class="detail-text">รุ่น</span>
              <v-text-field
                v-model.trim="form.vehicleModel"
                placeholder="เช่น Wave, Vios"
                v-bind="tfProps"
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="form.vehicleType">
              <span class="detail-text">สี</span>
              <v-text-field
                v-model.trim="form.vehicleColor"
                placeholder="เช่น ดำ, แดง"
                v-bind="tfProps"
              />
            </v-col>

            <!-- อัพโหลดรูปยานพาหนะคู่คนขับ (ทุกประเภท) -->
            <v-col cols="12" v-if="form.vehicleType">
              <span class="detail-text"
                ><span class="req">*</span>รูปยานพาหนะคู่คนขับ</span
              >
              <div class="upload-section">
                <div v-if="vehicleWithDriverPreview" class="preview-wrapper">
                  <v-img
                    :src="vehicleWithDriverPreview"
                    class="preview-image"
                    contain
                    max-height="200"
                  />
                  <v-btn
                    icon
                    small
                    class="remove-btn"
                    @click="removeVehicleWithDriver"
                  >
                    <v-icon color="white" size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="upload-placeholder" @click="triggerVehicleWithDriverInput">
                  <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                  <span class="upload-text">คลิกเพื่ออัพโหลดรูปยานพาหนะคู่คนขับ</span>
                  <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                </div>
                <input
                  ref="vehicleWithDriverInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleVehicleWithDriverChange"
                />
              </div>
            </v-col>

            <!-- อัพโหลดบัตรประชาชน (เฉพาะจักรยาน) -->
            <v-col cols="12" v-if="isBicycle">
              <span class="detail-text"
                ><span class="req">*</span>บัตรประชาชน</span
              >
              <div class="upload-section">
                <div v-if="idCardPreview" class="preview-wrapper">
                  <v-img
                    :src="idCardPreview"
                    class="preview-image"
                    contain
                    max-height="200"
                  />
                  <v-btn
                    icon
                    small
                    class="remove-btn"
                    @click="removeIdCard"
                  >
                    <v-icon color="white" size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="upload-placeholder" @click="triggerIdCardInput">
                  <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                  <span class="upload-text">คลิกเพื่ออัพโหลดบัตรประชาชน</span>
                  <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                </div>
                <input
                  ref="idCardInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleIdCardChange"
                />
              </div>
            </v-col>

            <!-- อัพโหลดเอกสารรถ (เฉพาะรถยนต์และรถมอไซต์) -->
            <v-col
              cols="12"
              v-if="form.vehicleType && !isBicycle"
            >
              <span class="detail-text"
                ><span class="req">*</span>เอกสารรถ (เช่น ใบจดทะเบียนรถ)</span
              >
              <div class="upload-section">
                <div v-if="vehicleDocPreview" class="preview-wrapper">
                  <v-img
                    :src="vehicleDocPreview"
                    class="preview-image"
                    contain
                    max-height="200"
                  />
                  <v-btn
                    icon
                    small
                    class="remove-btn"
                    @click="removeVehicleDoc"
                  >
                    <v-icon color="white" size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="upload-placeholder" @click="triggerVehicleDocInput">
                  <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                  <span class="upload-text">คลิกเพื่ออัพโหลดเอกสาร</span>
                  <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                </div>
                <input
                  ref="vehicleDocInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleVehicleDocChange"
                />
              </div>
            </v-col>

            <!-- อัพโหลดใบขับขี่ (เฉพาะรถยนต์และรถมอไซต์) -->
            <v-col
              cols="12"
              v-if="form.vehicleType && !isBicycle"
            >
              <span class="detail-text"
                ><span class="req">*</span>ใบขับขี่</span
              >
              <div class="upload-section">
                <div v-if="driverLicensePreview" class="preview-wrapper">
                  <v-img
                    :src="driverLicensePreview"
                    class="preview-image"
                    contain
                    max-height="200"
                  />
                  <v-btn
                    icon
                    small
                    class="remove-btn"
                    @click="removeDriverLicense"
                  >
                    <v-icon color="white" size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="upload-placeholder" @click="triggerDriverLicenseInput">
                  <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                  <span class="upload-text">คลิกเพื่ออัพโหลดใบขับขี่</span>
                  <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                </div>
                <input
                  ref="driverLicenseInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleDriverLicenseChange"
                />
              </div>
            </v-col>

            <!-- อัพโหลดใบขับขี่ (รย.) สำหรับการรับส่งผู้โดยสาร (ถ้ามี — admin จะพิจารณาเปิด service ให้ตอน verify) -->
            <v-col
              cols="12"
              v-if="form.vehicleType && !isBicycle"
            >
              <span class="detail-text"
                >ใบขับขี่ (รย.) สำหรับการรับส่งผู้โดยสาร <span style="color:#6B7280;font-size:12px">(ถ้ามี)</span></span
              >
              <div class="upload-section">
                <div v-if="passengerLicensePreview" class="preview-wrapper">
                  <v-img
                    :src="passengerLicensePreview"
                    class="preview-image"
                    contain
                    max-height="200"
                  />
                  <v-btn
                    icon
                    small
                    class="remove-btn"
                    @click="removePassengerLicense"
                  >
                    <v-icon color="white" size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="upload-placeholder" @click="triggerPassengerLicenseInput">
                  <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                  <span class="upload-text">คลิกเพื่ออัพโหลดใบขับขี่ (รย.)</span>
                  <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                </div>
                <input
                  ref="passengerLicenseInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handlePassengerLicenseChange"
                />
              </div>
            </v-col>

            <!-- หนังสือยินยอมจากเจ้าของรถ (เฉพาะรถยนต์/มอเตอร์ไซค์ — ถ้ามี) -->
            <v-col
              cols="12"
              v-if="form.vehicleType && !isBicycle"
            >
              <span class="detail-text"
                >หนังสือยินยอมจากเจ้าของรถ <span style="color:#6B7280;font-size:12px">(ถ้ามี)</span></span
              >
              <p class="upload-note">
                * กรณีชื่อเจ้าของรถไม่ตรงกับชื่อผู้ขับรถ ต้องมีหนังสือรับรองยินยอมการใช้รถจากเจ้าของรถแนบ
              </p>
              <div class="upload-section">
                <div v-if="ownerConsentPreview" class="preview-wrapper">
                  <v-img
                    :src="ownerConsentPreview"
                    class="preview-image"
                    contain
                    max-height="200"
                  />
                  <v-btn
                    icon
                    small
                    class="remove-btn"
                    @click="removeOwnerConsent"
                  >
                    <v-icon color="white" size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-else class="upload-placeholder" @click="triggerOwnerConsentInput">
                  <v-icon size="48" color="#9ca3af">mdi-file-image-outline</v-icon>
                  <span class="upload-text">คลิกเพื่ออัพโหลดหนังสือยินยอม</span>
                  <span class="upload-hint">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</span>
                </div>
                <input
                  ref="ownerConsentInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleOwnerConsentChange"
                />
              </div>
            </v-col>

            <!-- ปุ่ม -->
            <v-col cols="12" class="mt-3 d-flex justify-space-between">
              <v-btn class="btn-secondary" height="40" @click="handleBack">
                ย้อนกลับ
              </v-btn>
              <v-btn class="btn-primary" height="40" @click="handleNext">
                ถัดไป
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </div>
  </div>
</template>

<script>
import swalAlert from "@/services/alert/swalServices";
import { riderApi } from "@/services/api/api";

export default {
  name: "RegisterRiderVehicle",
  props: {
    value: {
      type: Object,
      default: () => ({
        vehicleType: "",
        licensePlate: "",
        vehicleBrand: "",
        vehicleModel: "",
        vehicleColor: "",
        idCardFile: null,
        vehicleDocFile: null,
        driverLicenseFile: null,
        passengerLicenseFile: null,
        vehicleWithDriverFile: null,
        ownerConsentFile: null,
        vehicleTypeId: "",
      }),
    },
  },

  data() {
    return {
      valid: false,
      form: { ...this.value },
      idCardPreview: null,
      vehicleDocPreview: null,
      driverLicensePreview: null,
      passengerLicensePreview: null,
      vehicleWithDriverPreview: null,
      ownerConsentPreview: null,

      tfProps: { outlined: true, hideDetails: "auto", dense: true },
      selProps: {
        outlined: true,
        hideDetails: "auto",
        menuProps: { offsetY: true },
        dense: true,
        "append-icon": "",
      },

      vehicleTypes: [],
      req: (v) => !!v || "กรุณากรอกข้อมูล",

      chev: require("@/assets/select/expand_icon.png"),
    };
  },

  computed: {
    isBicycle() {
      const selected = this.vehicleTypes.find(v => v.value === this.form.vehicleTypeId);
      const name = String((selected && selected.text) || this.form.vehicleType || "").toLowerCase();
      if (name.includes("motorcycle") || name.includes("จักรยานยนต์")) return false;
      return name.includes("bicycle") || name.includes("จักรยาน");
    },
  },

  mounted() {
    this.fetchVehicleTypes();
  },

  methods: {
    async fetchVehicleTypes() {
      try {
        const res = await riderApi.get("/api/v1/vehicleType", { skipAuth: true });
        if (res.data && res.data.data) {
          const companies = res.data.data;
          this.vehicleTypes = companies
            .filter(c => c.ship && c.ship.isRiderFlow)
            .map(c => ({ text: c.name, value: c.id }));
        }
      } catch (error) {
        console.error("Error fetching vehicle types:", error);
      }
    },
    handleBack() {
      this.$emit("input", { ...this.form });
      this.$emit("back");
    },
    onVehicleTypeChange(val) {
      const selected = this.vehicleTypes.find(v => v.value === val);
      if (selected) {
        this.form.vehicleType = selected.text;
      }
      
      // Reset form when vehicle type changes
      if (this.isBicycle) {
        this.form.vehicleDocFile = null;
        this.form.driverLicenseFile = null;
        this.form.passengerLicenseFile = null;
        this.vehicleDocPreview = null;
        this.driverLicensePreview = null;
        this.passengerLicensePreview = null;
      } else {
        this.form.idCardFile = null;
        this.idCardPreview = null;
      }
    },
    handleNext() {
      this.$refs.form.validate();
      if (!this.valid) return;

      // Validation: รูปยานพาหนะคู่คนขับ (ทุกประเภท)
      if (!this.form.vehicleWithDriverFile) {
        swalAlert.Error("กรุณาอัพโหลดรูปยานพาหนะคู่คนขับ");
        return;
      }

      // Validation for bicycle
      if (this.isBicycle) {
        if (!this.form.idCardFile) {
          swalAlert.Error("กรุณาอัพโหลดบัตรประชาชน");
          return;
        }
        this.$emit("next", { ...this.form });
        return;
      }

      // Validation for car/motorcycle
      if (!this.form.vehicleDocFile) {
        swalAlert.Error("กรุณาอัพโหลดเอกสารรถ");
        return;
      }
      if (!this.form.driverLicenseFile) {
        swalAlert.Error("กรุณาอัพโหลดใบขับขี่");
        return;
      }
      this.$emit("next", { ...this.form });
    },
    triggerIdCardInput() {
      this.$refs.idCardInput?.click();
    },
    triggerVehicleDocInput() {
      this.$refs.vehicleDocInput?.click();
    },
    triggerDriverLicenseInput() {
      this.$refs.driverLicenseInput?.click();
    },
    triggerPassengerLicenseInput() {
      this.$refs.passengerLicenseInput?.click();
    },
    triggerVehicleWithDriverInput() {
      this.$refs.vehicleWithDriverInput?.click();
    },
    triggerOwnerConsentInput() {
      this.$refs.ownerConsentInput?.click();
    },
    handleIdCardChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        swalAlert.Error("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        swalAlert.Error("ขนาดไฟล์ต้องไม่เกิน 5 MB");
        event.target.value = "";
        return;
      }

      if (this.idCardPreview && this.idCardPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.idCardPreview);
      }

      this.form.idCardFile = file;
      this.idCardPreview = URL.createObjectURL(file);
      event.target.value = "";
    },
    handleVehicleDocChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        swalAlert.Error("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        swalAlert.Error("ขนาดไฟล์ต้องไม่เกิน 5 MB");
        event.target.value = "";
        return;
      }

      if (this.vehicleDocPreview && this.vehicleDocPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.vehicleDocPreview);
      }

      this.form.vehicleDocFile = file;
      this.vehicleDocPreview = URL.createObjectURL(file);
      event.target.value = "";
    },
    handleDriverLicenseChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        swalAlert.Error("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        swalAlert.Error("ขนาดไฟล์ต้องไม่เกิน 5 MB");
        event.target.value = "";
        return;
      }

      if (this.driverLicensePreview && this.driverLicensePreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.driverLicensePreview);
      }

      this.form.driverLicenseFile = file;
      this.driverLicensePreview = URL.createObjectURL(file);
      event.target.value = "";
    },
    handlePassengerLicenseChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        swalAlert.Error("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        swalAlert.Error("ขนาดไฟล์ต้องไม่เกิน 5 MB");
        event.target.value = "";
        return;
      }

      if (this.passengerLicensePreview && this.passengerLicensePreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.passengerLicensePreview);
      }

      this.form.passengerLicenseFile = file;
      this.passengerLicensePreview = URL.createObjectURL(file);
      event.target.value = "";
    },
    removeIdCard() {
      if (this.idCardPreview && this.idCardPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.idCardPreview);
      }
      this.form.idCardFile = null;
      this.idCardPreview = null;
      if (this.$refs.idCardInput) {
        this.$refs.idCardInput.value = "";
      }
    },
    removeVehicleDoc() {
      if (this.vehicleDocPreview && this.vehicleDocPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.vehicleDocPreview);
      }
      this.form.vehicleDocFile = null;
      this.vehicleDocPreview = null;
      if (this.$refs.vehicleDocInput) {
        this.$refs.vehicleDocInput.value = "";
      }
    },
    removeDriverLicense() {
      if (this.driverLicensePreview && this.driverLicensePreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.driverLicensePreview);
      }
      this.form.driverLicenseFile = null;
      this.driverLicensePreview = null;
      if (this.$refs.driverLicenseInput) {
        this.$refs.driverLicenseInput.value = "";
      }
    },
    removePassengerLicense() {
      if (this.passengerLicensePreview && this.passengerLicensePreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.passengerLicensePreview);
      }
      this.form.passengerLicenseFile = null;
      this.passengerLicensePreview = null;
      if (this.$refs.passengerLicenseInput) {
        this.$refs.passengerLicenseInput.value = "";
      }
    },
    handleVehicleWithDriverChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        swalAlert.Error("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        swalAlert.Error("ขนาดไฟล์ต้องไม่เกิน 5 MB");
        event.target.value = "";
        return;
      }

      if (this.vehicleWithDriverPreview && this.vehicleWithDriverPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.vehicleWithDriverPreview);
      }

      this.form.vehicleWithDriverFile = file;
      this.vehicleWithDriverPreview = URL.createObjectURL(file);
      event.target.value = "";
    },
    removeVehicleWithDriver() {
      if (this.vehicleWithDriverPreview && this.vehicleWithDriverPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.vehicleWithDriverPreview);
      }
      this.form.vehicleWithDriverFile = null;
      this.vehicleWithDriverPreview = null;
      if (this.$refs.vehicleWithDriverInput) {
        this.$refs.vehicleWithDriverInput.value = "";
      }
    },
    handleOwnerConsentChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        swalAlert.Error("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        swalAlert.Error("ขนาดไฟล์ต้องไม่เกิน 5 MB");
        event.target.value = "";
        return;
      }

      if (this.ownerConsentPreview && this.ownerConsentPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.ownerConsentPreview);
      }

      this.form.ownerConsentFile = file;
      this.ownerConsentPreview = URL.createObjectURL(file);
      event.target.value = "";
    },
    removeOwnerConsent() {
      if (this.ownerConsentPreview && this.ownerConsentPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.ownerConsentPreview);
      }
      this.form.ownerConsentFile = null;
      this.ownerConsentPreview = null;
      if (this.$refs.ownerConsentInput) {
        this.$refs.ownerConsentInput.value = "";
      }
    },
  },

  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(v) {
        if (!v) return;
        this.form = { ...this.form, ...v };

        if (v.idCardFile && v.idCardFile instanceof File) {
          if (this.idCardPreview && this.idCardPreview.startsWith("blob:")) {
            URL.revokeObjectURL(this.idCardPreview);
          }
          this.idCardPreview = URL.createObjectURL(v.idCardFile);
        }
        if (v.vehicleDocFile && v.vehicleDocFile instanceof File) {
          if (this.vehicleDocPreview && this.vehicleDocPreview.startsWith("blob:")) {
            URL.revokeObjectURL(this.vehicleDocPreview);
          }
          this.vehicleDocPreview = URL.createObjectURL(v.vehicleDocFile);
        }
        if (v.driverLicenseFile && v.driverLicenseFile instanceof File) {
          if (this.driverLicensePreview && this.driverLicensePreview.startsWith("blob:")) {
            URL.revokeObjectURL(this.driverLicensePreview);
          }
          this.driverLicensePreview = URL.createObjectURL(v.driverLicenseFile);
        }
        if (v.passengerLicenseFile && v.passengerLicenseFile instanceof File) {
          if (this.passengerLicensePreview && this.passengerLicensePreview.startsWith("blob:")) {
            URL.revokeObjectURL(this.passengerLicensePreview);
          }
          this.passengerLicensePreview = URL.createObjectURL(v.passengerLicenseFile);
        }
        if (v.vehicleWithDriverFile && v.vehicleWithDriverFile instanceof File) {
          if (this.vehicleWithDriverPreview && this.vehicleWithDriverPreview.startsWith("blob:")) {
            URL.revokeObjectURL(this.vehicleWithDriverPreview);
          }
          this.vehicleWithDriverPreview = URL.createObjectURL(v.vehicleWithDriverFile);
        }
        if (v.ownerConsentFile && v.ownerConsentFile instanceof File) {
          if (this.ownerConsentPreview && this.ownerConsentPreview.startsWith("blob:")) {
            URL.revokeObjectURL(this.ownerConsentPreview);
          }
          this.ownerConsentPreview = URL.createObjectURL(v.ownerConsentFile);
        }
      },
    },
  },

  beforeDestroy() {
    if (this.idCardPreview && this.idCardPreview.startsWith("blob:")) {
      URL.revokeObjectURL(this.idCardPreview);
    }
    if (this.vehicleDocPreview && this.vehicleDocPreview.startsWith("blob:")) {
      URL.revokeObjectURL(this.vehicleDocPreview);
    }
    if (this.driverLicensePreview && this.driverLicensePreview.startsWith("blob:")) {
      URL.revokeObjectURL(this.driverLicensePreview);
    }
    if (this.passengerLicensePreview && this.passengerLicensePreview.startsWith("blob:")) {
      URL.revokeObjectURL(this.passengerLicensePreview);
    }
    if (this.vehicleWithDriverPreview && this.vehicleWithDriverPreview.startsWith("blob:")) {
      URL.revokeObjectURL(this.vehicleWithDriverPreview);
    }
    if (this.ownerConsentPreview && this.ownerConsentPreview.startsWith("blob:")) {
      URL.revokeObjectURL(this.ownerConsentPreview);
    }
  },
};
</script>

<style scoped>
.register-vehicle {
  padding-bottom: 90px;
}
.form-container {
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  padding: 0 16px;
}
.form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #333;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 15px;
}
.form-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
}
.req {
  color: #e11d48;
  margin-right: 4px;
}
.section-card {
  padding: 24px;
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(2, 6, 23, 0.06);
}
.section-card :deep(p.muted) {
  text-align: left !important;
  line-height: 1.6;
  color: #4b5563;
  margin-bottom: 16px;
}
.detail-text {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #1f2a39;
  line-height: 1.4;
  margin-bottom: 8px;
}
:deep(.v-input) {
  margin-bottom: 8px !important;
}
:deep(.v-input--dense .v-input__slot) {
  min-height: 45px !important;
  height: 45px !important;
  padding: 0 10px !important;
}
:deep(.v-select.v-input--dense .v-input__slot) {
  min-height: 45px !important;
  height: 45px !important;
  padding: 0 10px !important;
}
:deep(.v-select__selections) {
  min-height: 45px !important;
  height: 45px !important;
  align-items: center !important;
  font-size: 14px !important;
}
:deep(.v-menu__content .v-list-item) {
  min-height: 28px !important;
  height: 28px !important;
}
:deep(.v-menu__content .v-list-item__title) {
  font-size: 14px !important;
  line-height: 1.3 !important;
}
.v-text-field--outlined >>> fieldset {
  border-color: #e5e7eb !important;
  border-radius: 8px !important;
}
.btn-primary {
  background: #F4811F !important;
  color: #fff !important;
  border-radius: 8px !important;
}
.btn-secondary {
  background: #e5e7eb !important;
  color: #111827 !important;
  border-radius: 8px !important;
}
.v-select .icon-rotate {
  transition: transform 0.3s ease;
  transform: rotate(0);
}
.v-select.v-select--is-menu-active .icon-rotate {
  transform: rotate(180deg);
}
.upload-section {
  margin-top: 8px;
}
.preview-wrapper {
  position: relative;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #f9fafb;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-image {
  max-width: 100%;
  border-radius: 4px;
}
.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6) !important;
  z-index: 1;
}
.upload-placeholder {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.upload-placeholder:hover {
  border-color: #F4811F;
  background: #fef2f2;
}
.upload-text {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  word-break: break-word;
  overflow-wrap: break-word;
  text-align: center;
  max-width: 100%;
}
.upload-hint {
  display: block;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  max-width: 100%;
}
.upload-note {
  font-size: 12px;
  color: #b91c1c;
  margin: 0 0 8px 0;
  line-height: 1.4;
}
@media (max-width: 599px) {
  .register-vehicle { padding-bottom: 60px; }
  .section-card { padding: 16px 12px; }
  .btn-primary, .btn-secondary { min-height: 44px !important; }
  .upload-placeholder {
    padding: 20px 12px;
    min-height: 140px;
  }
  .preview-wrapper { min-height: 140px; }
  .upload-text { font-size: 12px; }
}
</style>
